import React, { ButtonHTMLAttributes, CSSProperties, forwardRef } from 'react';
import styled from '@emotion/styled';
import { semantic_colors, text_styles } from '../../foundation';
import { buildCSSWithLength, CSSValueWithLength, getAutoColorCode } from '../../styles';
import { Stack } from '../../components';
import { TextButton } from '../../components/button';
import { IconSidebarClose, IconSidebarOpen } from '../../components/icons/generated';
import { top_bar_action_button_css } from './styles';

export type TopBarSidebarToggleButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-expanded'
> & {
  'aria-expanded': boolean | 'true' | 'false';
};

/** 데스크톱 TopBar에서 SNB의 열림 상태를 전환하는 버튼입니다. 열림 상태는 `aria-expanded`로 전달합니다. */
const SidebarToggleButton = forwardRef<HTMLButtonElement, TopBarSidebarToggleButtonProps>(
  ({ type = 'button', 'aria-expanded': ariaExpanded, 'aria-label': ariaLabel, ...props }, ref) => {
    const isExpanded = ariaExpanded === true || ariaExpanded === 'true';
    const ToggleIcon = isExpanded ? IconSidebarClose : IconSidebarOpen;

    return (
      <StyledSidebarToggleButton
        ref={ref}
        type={type}
        aria-expanded={ariaExpanded}
        aria-label={ariaLabel ?? (isExpanded ? '사이드바 닫기' : '사이드바 열기')}
        {...props}
      >
        <ToggleIcon size={20} color='currentColor' aria-hidden />
      </StyledSidebarToggleButton>
    );
  },
);

SidebarToggleButton.displayName = 'TopBar.SidebarToggleButton';

const StyledSidebarToggleButton = styled.button`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 2px;
  border: 0;
  border-radius: 4px;
  color: ${semantic_colors.content.primary_inverse};
  background-color: transparent;
  cursor: pointer;

  &:hover {
    color: ${getAutoColorCode(semantic_colors.content.primary_inverse).hover};
    background-color: ${getAutoColorCode(semantic_colors.background.surface_inverse).hover};
  }

  &:active {
    color: ${getAutoColorCode(semantic_colors.content.primary_inverse).pressed};
    background-color: ${getAutoColorCode(semantic_colors.background.surface_inverse).pressed};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const TopBar = {
  Container: styled.div<{ backgroundColor?: CSSProperties['backgroundColor']; height?: CSSValueWithLength }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-left: 16px;
    padding-right: 8px;
    ${({ height }) => buildCSSWithLength('height', height ?? 44)};
    background-color: ${({ backgroundColor }) => backgroundColor ?? semantic_colors.background.surface_inverse};
  `,
  SidebarToggleButton,
  Title: styled.a`
    display: flex;
    align-items: center;
  `,
  ActionArea: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 0px;
  `,
  ActionButton: styled(TextButton)(top_bar_action_button_css),
  ActionIconButton: styled.span`
    position: relative;
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: ${semantic_colors.content.primary_inverse};
    &:hover {
      color: ${getAutoColorCode(semantic_colors.content.primary_inverse).hover};
      background-color: ${getAutoColorCode(semantic_colors.background.surface_inverse).hover};
    }
    &:active {
      color: ${getAutoColorCode(semantic_colors.content.primary_inverse).pressed};
      background-color: ${getAutoColorCode(semantic_colors.background.surface_inverse).pressed};
    }
    & + & {
      margin-left: 1px;
    }
  `,
  NumberIcon: styled(Stack)<{ size?: 'large' | 'medium'; value: number }>`
    position: absolute;
    border-radius: ${({ value }) => (value > 9 ? '75px' : '50%')};
    align-items: center;
    justify-content: center;
    z-index: 1;
    pointer-events: none;
    background-color: ${semantic_colors.brand.primary};
    color: ${semantic_colors.content.primary_inverse};
    ${({ size, value }) =>
      size === 'large'
        ? `
        padding: 0 4px;
        height: 18px;
        ${value <= 9 ? 'width: 18px;' : ''}
        top: 1px;
        left: 27px;
        transform: translateX(-50%);
        ${text_styles.Caption_11_SemiBold};
      `
        : `
        padding: 2px 3px;
        height: 13px;
        ${value <= 9 ? 'width: 13px;' : ''}
        top: 4px;
        left: 22px;
        transform: translateX(-50%);
        ${text_styles.Caption_10_SemiBold};
      `}
  `,
};
